import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { Container, Card, Form, Spinner, Row, Col, Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAsterisk } from '@fortawesome/free-solid-svg-icons';
import FormFieldError from 'src/components/general/FormFieldError';
import { tryPostUser, tryGetListUserProfile } from 'src/redux/user/userActionCreator';
import { getUserProfileListData, getUserProfileListIsFetching, getUserCreateIsFetching, getAddUserToProfileIsFetching } from 'src/redux/user/userReducer';
import { USER_LIST } from 'src/utils/constants';
import { generatePassword, joinLastNameName } from 'src/utils/utils';
import { setAssignProfiles } from 'src/utils/usersProfiles';
import { tryAddAllUsersAdministrativeService, tryAddNewUserAdministrativeService, tryListAllAdministrativeServices } from 'src/redux/administrativeService/administrativeServiceActionCreator';
import { tryGetUserList } from 'src/redux/user/userActionCreator';
import { getAdministrativeServiceListAllData, getAdministrativeServiceListAllIsFetching } from 'src/redux/administrativeService/administrativeServiceReducer';
import { getAccessToken } from 'src/redux/login/loginReducer';
import { postUserOrganisms } from 'src/services/userServices';
import { showSuccess } from 'src/redux/globalData/globalDataActionCreator';
import { useJurisdictions } from './utils/useJurisdictions';
import { createValidationRules, isOrganismSelectionRequired, isServiceSelectionRequired } from './utils/UserValidations';
import OrganismTreeSelect from './utils/OrganismTreeSelect';
import ServiceMultiSelect from './utils/ServiceMultiSelect';

const UserNewPage = () => {
	const dispatch = useDispatch();
	const {
		register,
		handleSubmit,
		errors,
		getValues
	} = useForm();
	const { 
		jurisdictionData, 
		isLoading: jurisdictionsLoading,
		fetchJurisdictions,
		processSelectedOrganisms 
	} = useJurisdictions();
    const validationRules = createValidationRules();
	const accessToken = useSelector(state => getAccessToken(state));
	const [selectedOrganisms, setSelectedOrganisms] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

	// Estados y Opciones para Cuentas Especiales
	const [specialAccountsFilter, setSpecialAccountsFilter] = useState('0');
	const SPECIAL_ACCOUNTS_OPTIONS = [
		{ value: '1', label: 'Sin cuentas especiales' },
		{ value: '2', label: 'Solo cuentas especiales' },
		{ value: '3', label: 'Incluir cuentas especiales' }
	];
	
	// Ver lista de permisos asociados, filtrarlos por autorizados y validar	
	const rawProfileData = useSelector(getUserProfileListData);
	const listProfileData = useMemo(() => {
		if (!rawProfileData?.length) return [];
		return [...rawProfileData].sort((profile1, profile2) => {
			const name1 = profile1.name?.toUpperCase() || '';
			const name2 = profile2.name?.toUpperCase() || '';
			return name1.localeCompare(name2);
		});
	}, [rawProfileData]);
	const listProfileIsFetching = useSelector(state => getUserProfileListIsFetching(state));
	const profileNameArray = setAssignProfiles();
	let profileNameArrayRecords = listProfileData?.filter(item => profileNameArray?.includes(item?.name));
	const [selectedProfile, setSelectedProfile] = useState(false);
	const requiresServices = isServiceSelectionRequired(selectedProfile);
	const requiresOrganisms = isOrganismSelectionRequired(selectedProfile);

	//Services Administrative
	const administrativeService = useSelector(state => getAdministrativeServiceListAllData(state))?.records?.sort((a, b) => a.code > b.code ? 1 : -1);
	const administrativeServiceIsFetching = useSelector(state => getAdministrativeServiceListAllIsFetching(state));
	const postUserIsFetching = useSelector(state => getUserCreateIsFetching(state));
	const postAddUserToProfileIsFetching = useSelector(state => getAddUserToProfileIsFetching(state));
	const userCreateIsFetching = (postUserIsFetching || postAddUserToProfileIsFetching);

	// Multiselect de Servicios Administrativos
	const [selectedService, setSelectedService] = useState([]);
	const serviceOptions = useMemo(() => {
		if (!administrativeService?.length) return [];
		return administrativeService.map(service => ({
			label: `${service?.code} - ${service?.shortName}`,
			value: service?.id
		}));
	}, [administrativeService]);

	const onFormSubmit = async (data) => {
        // Prevenir envíos multiples
        if (isSubmitting) {
            console.log('Formulario ya está siendo procesado');
            return;
        }

		try {
			setIsSubmitting(true);

			// Preparar datos del usuario
			const userData = {
				name: joinLastNameName(data?.lastName, data?.name)?.toUpperCase(),
				alias: data?.name,
				password: generatePassword(),
				email: data?.email,
				description: undefined,
				profileId: data?.profileId
			};
	
			// PASO 1: SIEMPRE crear el usuario primero
			const userResponse = await dispatch(tryPostUser(
				userData.name,
				userData.alias,
				userData.password,
				userData.email,
				userData.description,
				userData.profileId
			));
			if (userResponse?.status !== 200) {
				console.error('Error creando usuario:', userResponse);
				return;
			}
			const userId = userResponse?.data?.id;

			// PASO 2: SIEMPRE asignar servicios al recibir el userId
			const userIdArray = [userId];
			if (requiresServices) {
				// Perfiles específicos que requieren selección de servicios
				const selectedAdministrativeService = selectedService?.map(item => item.value);
				await dispatch(tryAddNewUserAdministrativeService(userIdArray, selectedAdministrativeService));
			} else {
				// Perfiles que reciben todos los servicios administrativos
				await dispatch(tryAddAllUsersAdministrativeService(userId));
			}

			// PASO 3: Dependiendo del perfil, asignar organismos
			if (selectedProfile === 'Consulta por Organismo') {
				const selectedValues = selectedOrganisms || [];
				if (selectedValues.length > 0) {
					const organismIds = processSelectedOrganisms(selectedValues);
					if (organismIds.length > 0) {
						await postUserOrganisms(accessToken, userId, organismIds, specialAccountsFilter);
					} else {
						console.warn('No se encontraron organismos válidos para asignar');
					}
				} else {
					console.warn('No se seleccionaron organismos para el perfil "Consulta por Organismo"');
				}
			}
	
			// PASO 4: Redirigir o mostrar mensaje de éxito
			showSuccess('Usuario registrado correctamente.');
			dispatch(push(USER_LIST));
		} catch (error) {
			console.error('Error in form submission:', error);
		} finally {
            setIsSubmitting(false);
        }
	};

	const onChangeProfile = item => {
		setSelectedProfile(item.target.options[item.target.selectedIndex].text);
		setSelectedService([]);
		setSelectedOrganisms([]);
	}

	// Validar boton de Guardado
	const isSelectedService = selectedService.length === 0;
	const isSelectedOrganisms = !selectedOrganisms || selectedOrganisms.length === 0;
	const isSubmitDisabled = useMemo(() => {
		if (userCreateIsFetching || isSubmitting) return true;
		
		// Validar campos básicos requeridos SIEMPRE
		const formValues = getValues();
		if (!formValues.lastName?.trim()) return true;
		if (!formValues.name?.trim()) return true;
		if (!formValues.email?.trim()) return true;
		if (!formValues.profileId) return true;
		
		// Para perfiles específicos: validar que requieren servicios
		if (requiresServices) {
			if (isSelectedService) return true;
		}
		
		// Para "Consulta por Organismo": validar organismos también
		if (requiresOrganisms) {
			if (isSelectedOrganisms) return true;
		}
		
		return false;
	}, [
		userCreateIsFetching,
		isSubmitting,
		selectedProfile,
		requiresServices,
		isSelectedService,
		isSelectedOrganisms
	]);

	useEffect(() => {
		dispatch(tryGetListUserProfile());
		dispatch(tryGetUserList());
		dispatch(tryListAllAdministrativeServices());
	}, []);

	useEffect(() => {
	  	fetchJurisdictions();
	}, [fetchJurisdictions]);

	return <>
		<Container fluid>
			<Card className='mb-3'>
				<Card.Header>
					<h6 className='mb-0'>Nuevo usuario</h6>
				</Card.Header>
				<Card.Body >
					<span className='mb-3 text-danger d-flex mandatory-label justify-content-end font-weight-bold font-italic align-items-center'>
						<FontAwesomeIcon icon={faAsterisk} className="mr-2 text-danger mandatory-asterisk" />
						Obligatorio
					</span>
					<Form onSubmit={handleSubmit(onFormSubmit)}>
						<Form.Group as={Row} controlId='alias'>
							<Form.Label className='text-right text-black-color pr-0 pl-0 d-flex mandatory-label' column sm={4}>
								<FontAwesomeIcon icon={faAsterisk} className='mb-2 mr-1 text-danger mandatory-asterisk' />
								Apellido:
							</Form.Label>
							<Col sm={4}>
								<Form.Control
									ref={register(validationRules.lastName)}
									type='text'
									placeholder='Apellido'
									name='lastName'
									maxLength={50}
									minLength={3}
								/>
								<FormFieldError errors={errors?.lastName} />
							</Col>
						</Form.Group>

						<Form.Group as={Row}>
							<Form.Label className='text-right text-black-color pr-0 pl-0 d-flex mandatory-label' column sm={4}>
								<FontAwesomeIcon icon={faAsterisk} className='mb-2 mr-1 text-danger mandatory-asterisk' />
								Nombre:
							</Form.Label>
							<Col sm={4}>
								<Form.Control
									ref={register(validationRules.name)}
									type='text'
									placeholder='Nombre'
									name='name'
									id='name'
									maxLength={50}
									minLength={3}
								/>
								<FormFieldError errors={errors?.name} />
							</Col>
						</Form.Group>

						<Form.Group as={Row}>
							<Form.Label className='text-right text-black-color pr-0 pl-0 d-flex mandatory-label' column sm={4}>
								<FontAwesomeIcon icon={faAsterisk} className='mb-2 mr-1 text-danger mandatory-asterisk' />
								Correo electrónico:
							</Form.Label>
							<Col sm={4}>
								<Form.Control
									ref={register(validationRules.email)}
									type='email'
									placeholder='Correo electrónico'
									name='email'
									id='email'
									pattern="^[a-z0-9!#$%&'*+\/=?^_`\{\|\}~\-]+([a-z0-9!#$%&'*+\/=?^_`\{\|\}~\-\.])*@([a-z0-9]([a-z0-9]*[a-z0-9])?\.)+[a-z0-9]([a-z0-9]*[a-z0-9])?$"
								/>
								<FormFieldError errors={errors?.email} />
							</Col>
						</Form.Group>

						<Form.Group as={Row} >
							<Form.Label className='text-right text-black-color pr-0 pl-0 d-flex mandatory-label' column sm='4'>
								<FontAwesomeIcon icon={faAsterisk} className='mb-2 mr-1 text-danger mandatory-asterisk' />
								Grupo:
							</Form.Label>
							<Col sm='4'>
								<Form.Control
									as='select'
									name='profileId'
									id='profileId'
									ref={register(validationRules.profile)}
									onChange={event => onChangeProfile(event)}
								>
									<option className='text-black-color' value={''}>
										Seleccione una opción...
									</option>
									{profileNameArrayRecords?.map(profile =>
										<option className='text-black-color' value={profile.id} key={profile?.id}>
											{`${profile.name || ''}`}
										</option>)
									}
								</Form.Control>
								<FormFieldError errors={errors?.profileId} />
							</Col>
							<Col sm='4' className='py-1'>
								{listProfileIsFetching &&
									<Spinner animation='border' size='sm' className='my-2 spinner-border text-danger' />
								}
							</Col>
						</Form.Group>

						{requiresServices &&
                            <ServiceMultiSelect
                                options={serviceOptions}
                                value={selectedService}
								setSelectedService={setSelectedService}
                                isLoading={administrativeServiceIsFetching}
                                required={true}
                                showLabel={true}
                                showValidationError={selectedService.length === 0}
                            />
						}

						{requiresOrganisms &&
							<>
								{/* MultiSelect de Organismos */}
								<OrganismTreeSelect
									treeData={jurisdictionData}
									value={selectedOrganisms}
									setSelectedOrganisms={setSelectedOrganisms}
									loading={jurisdictionsLoading}
									placeholder="Seleccione organismos"
								/>
                                
                                {/* Select de Cuentas Especiales */}
                                <Form.Group as={Row}>
                                    <Form.Label className='text-right text-black-color' column sm={4}>
										<FontAwesomeIcon icon={faAsterisk} className='mb-2 mr-1 text-danger mandatory-asterisk' />
                                        Cuentas especiales:
                                    </Form.Label>
                                    <Col sm={4}>
                                        <Form.Control
                                            as='select'
                                            value={specialAccountsFilter}
                                            onChange={(e) => setSpecialAccountsFilter(e.target.value)}
                                            className='text-black-color'
                                        >
                                            {SPECIAL_ACCOUNTS_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Col>
                                </Form.Group>
							</>
						}

						<div className='d-flex justify-content-around mt-4 mb-3'>
							<Button 
								variant='primary' 
								size='lg' 
								disabled={userCreateIsFetching || isSubmitting}
								onClick={() => dispatch(push(USER_LIST))}
							>
								Cancelar
							</Button>
							<span className={(userCreateIsFetching || isSubmitting ? '' : 'hidden')}>
								<Spinner className='spinner-border text-danger' animation='border' />
							</span>
							<Button 
								type='submit' 
								variant='success' 
								size='lg' 
								disabled={isSubmitDisabled}
							>
								{isSubmitting ? 'Guardando...' : 'Guardar'}
							</Button>
						</div>
					</Form>
				</Card.Body>
			</Card>
		</Container>
	</>
};

export default UserNewPage;